import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Printer, FileText, Building2, Package, Check, Send, Users } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SlipDiscountDialog, SlipDiscountLine } from "@/components/admin/SlipDiscountDialog";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDeliverySlips = () => {
  const navigate = useNavigate();
  const printRef = useRef(null);
  
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [showNewSlip, setShowNewSlip] = useState(false);
  const [showAddArticle, setShowAddArticle] = useState(false);
  const [printSlip, setPrintSlip] = useState(null);
  const [proCustomers, setProCustomers] = useState([]);
  const [showTransferDialog, setShowTransferDialog] = useState(null); // slip to transfer
  const [discountSlip, setDiscountSlip] = useState(null);
  
  // Form states
  const [newAccountName, setNewAccountName] = useState("");
  const [newArticleName, setNewArticleName] = useState("");
  const [newArticlePrice, setNewArticlePrice] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [lotArticle, setLotArticle] = useState("");
  const [lotQuantity, setLotQuantity] = useState(1);
  const [currentLotItems, setCurrentLotItems] = useState([]); // Articles du lot en cours
  const [pendingItems, setPendingItems] = useState([]); // Tous les articles du bon
  
  // Edit slip states
  const [editingSlip, setEditingSlip] = useState(null);
  const [editItems, setEditItems] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminAuth");
    return { Authorization: `Basic ${token}` };
  };

  useEffect(() => {
    const token = localStorage.getItem("adminAuth");
    if (!token) { navigate("/admin"); return; }
    fetchAccounts();
    fetchProCustomers();
  }, [navigate]);

  useEffect(() => {
    if (selectedAccount) fetchSlips(selectedAccount.id);
  }, [selectedAccount]);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/delivery-accounts`, { headers: getAuthHeaders() });
      setAccounts(res.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) { localStorage.removeItem("adminAuth"); navigate("/admin"); return; }
      toast.error("Erreur chargement comptes");
      setLoading(false);
    }
  };

  const fetchProCustomers = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/customers`, { headers: getAuthHeaders() });
      const pros = res.data.filter(c => c.is_pro);
      setProCustomers(pros);
    } catch (error) {
      console.error("Erreur chargement clients pro");
    }
  };

  const fetchSlips = async (accountId) => {
    try {
      const res = await axios.get(`${API}/api/admin/delivery-accounts/${accountId}/slips`, { headers: getAuthHeaders() });
      setSlips(res.data);
    } catch (error) {
      toast.error("Erreur chargement bons");
    }
  };

  const markAsPaid = async (slipId) => {
    try {
      const res = await axios.put(`${API}/api/admin/delivery-slips/${slipId}/mark-paid`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      if (selectedAccount) fetchSlips(selectedAccount.id);
    } catch (error) {
      toast.error("Erreur lors du marquage");
    }
  };

  const transferToCustomer = async (slipId, customerId) => {
    try {
      const res = await axios.put(`${API}/api/admin/delivery-slips/${slipId}/transfer`, { customer_id: customerId }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setShowTransferDialog(null);
      if (selectedAccount) fetchSlips(selectedAccount.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du transfert");
    }
  };

  const createAccount = async () => {
    if (!newAccountName.trim()) { toast.error("Nom requis"); return; }
    try {
      await axios.post(`${API}/api/admin/delivery-accounts`, { name: newAccountName, prices: [] }, { headers: getAuthHeaders() });
      toast.success("Compte créé");
      setShowNewAccount(false);
      setNewAccountName("");
      fetchAccounts();
    } catch (error) { toast.error("Erreur création"); }
  };

  const addArticleToAccount = async () => {
    if (!newArticleName.trim() || !newArticlePrice) { toast.error("Nom et prix requis"); return; }
    try {
      const updatedPrices = [...(selectedAccount.prices || []), { name: newArticleName, price: parseFloat(newArticlePrice) }];
      await axios.put(`${API}/api/admin/delivery-accounts/${selectedAccount.id}`, { prices: updatedPrices }, { headers: getAuthHeaders() });
      toast.success("Article ajouté");
      setShowAddArticle(false);
      setNewArticleName("");
      setNewArticlePrice("");
      const res = await axios.get(`${API}/api/admin/delivery-accounts`, { headers: getAuthHeaders() });
      setAccounts(res.data);
      setSelectedAccount(res.data.find(a => a.id === selectedAccount.id));
    } catch (error) { toast.error("Erreur ajout"); }
  };

  const deleteAccount = async (accountId) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    try {
      await axios.delete(`${API}/api/admin/delivery-accounts/${accountId}`, { headers: getAuthHeaders() });
      toast.success("Compte supprimé");
      if (selectedAccount?.id === accountId) { setSelectedAccount(null); setSlips([]); }
      fetchAccounts();
    } catch (error) { toast.error("Erreur suppression"); }
  };

  const addItemToPending = () => {
    if (!lotNumber.trim() || !lotArticle || lotQuantity < 1) { toast.error("Remplir tous les champs"); return; }
    const price = selectedAccount?.prices?.find(p => p.name === lotArticle)?.price || 0;
    // Ajouter à la liste du lot en cours
    setCurrentLotItems([...currentLotItems, { article: lotArticle, quantity: lotQuantity, unit_price: price }]);
    setLotArticle("");
    setLotQuantity(1);
    toast.success(`${lotQuantity}x ${lotArticle} ajouté au lot ${lotNumber}`);
  };

  const validateCurrentLot = () => {
    if (!lotNumber.trim()) { toast.error("Entrez un numéro de lot"); return; }
    if (currentLotItems.length === 0) { toast.error("Ajoutez au moins un article au lot"); return; }
    // Ajouter tous les articles du lot aux items du bon
    const itemsWithLotNumber = currentLotItems.map(item => ({ ...item, marking_number: lotNumber }));
    setPendingItems([...pendingItems, ...itemsWithLotNumber]);
    // Reset pour le prochain lot
    setCurrentLotItems([]);
    setLotNumber("");
    toast.success(`Lot ${lotNumber} validé !`);
  };

  const removeCurrentLotItem = (index) => {
    setCurrentLotItems(currentLotItems.filter((_, i) => i !== index));
  };

  const removePendingItem = (index) => {
    setPendingItems(pendingItems.filter((_, i) => i !== index));
  };

  const createSlip = async () => {
    if (pendingItems.length === 0) { toast.error("Ajoutez au moins un article"); return; }
    try {
      const res = await axios.post(`${API}/api/admin/delivery-slips`, { account_id: selectedAccount.id, items: pendingItems, notes: "" }, { headers: getAuthHeaders() });
      if (res.data.success && res.data.slip) {
        toast.success(`Bon ${res.data.slip.slip_number} créé avec ${pendingItems.length} articles !`, { duration: 5000 });
        // Ouvrir directement l'aperçu pour imprimer
        setPrintSlip(res.data.slip);
      } else {
        toast.success("Bon créé !");
      }
      setShowNewSlip(false);
      setPendingItems([]);
      setCurrentLotItems([]);
      setLotNumber("");
      fetchSlips(selectedAccount.id);
    } catch (error) { 
      console.error("Erreur création bon:", error);
      toast.error("Erreur création bon: " + (error.response?.data?.detail || error.message)); 
    }
  };

  const addItemsToSlip = async (slipId) => {
    if (!lotNumber.trim() || !lotArticle || lotQuantity < 1) { toast.error("Remplir tous les champs"); return; }
    const price = selectedAccount?.prices?.find(p => p.name === lotArticle)?.price || 0;
    try {
      await axios.put(`${API}/api/admin/delivery-slips/${slipId}/add-items`, {
        items: [{ marking_number: lotNumber, article: lotArticle, quantity: lotQuantity, unit_price: price }]
      }, { headers: getAuthHeaders() });
      toast.success("Article ajouté au bon !");
      setLotNumber("");
      setLotArticle("");
      setLotQuantity(1);
      fetchSlips(selectedAccount.id);
    } catch (error) { toast.error("Erreur ajout"); }
  };

  const deleteSlip = async (slipId) => {
    if (!window.confirm("Supprimer ce bon ?")) return;
    try {
      await axios.delete(`${API}/api/admin/delivery-slips/${slipId}`, { headers: getAuthHeaders() });
      toast.success("Bon supprimé");
      fetchSlips(selectedAccount.id);
    } catch (error) { toast.error("Erreur suppression"); }
  };

  // Ouvrir l'édition d'un bon
  const openEditSlip = (slip) => {
    setEditingSlip(slip);
    setEditItems([...slip.items]);
  };

  // Modifier un article dans l'édition
  const updateEditItem = (index, field, value) => {
    const newItems = [...editItems];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    setEditItems(newItems);
  };

  // Supprimer un article dans l'édition
  const removeEditItem = (index) => {
    if (editItems.length <= 1) { toast.error("Le bon doit contenir au moins un article"); return; }
    setEditItems(editItems.filter((_, i) => i !== index));
  };

  // Ajouter un article dans l'édition
  const addEditItem = () => {
    setEditItems([...editItems, { marking_number: "", article: "", quantity: 1, unit_price: 0, total: 0 }]);
  };

  // Sauvegarder les modifications
  const saveEditSlip = async () => {
    const validItems = editItems.filter(i => i.marking_number && i.article && i.quantity > 0);
    if (validItems.length === 0) { toast.error("Le bon doit contenir au moins un article valide"); return; }
    try {
      const res = await axios.put(`${API}/api/admin/delivery-slips/${editingSlip.id}`, {
        items: validItems.map(i => ({ marking_number: i.marking_number, article: i.article, quantity: i.quantity, unit_price: i.unit_price }))
      }, { headers: getAuthHeaders() });
      toast.success("Bon modifié !");
      setEditingSlip(null);
      setEditItems([]);
      fetchSlips(selectedAccount.id);
    } catch (error) { toast.error("Erreur modification: " + (error.response?.data?.detail || error.message)); }
  };

  const calcEditTotal = () => editItems.reduce((s, i) => s + (i.quantity * i.unit_price), 0);

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '', 'width=900,height=700');
    printWindow.document.write(`<html><head><title>Bon ${printSlip?.slip_number}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body { font-family: Arial, sans-serif; padding: 10px; font-size: 12px; }
        .header { text-align: center; margin-bottom: 20px; }
        .header h2 { margin: 5px 0; color: #db2777; }
        .info { margin-bottom: 15px; }
        .info p { margin: 3px 0; }
        .lot-section { margin-bottom: 15px; page-break-inside: avoid; }
        .lot-header { background: #fbcfe8; padding: 8px; font-weight: bold; color: #9d174d; border: 1px solid #f9a8d4; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #333; padding: 6px; text-align: left; font-size: 11px; }
        th { background: #fce7f3; }
        .lot-total { background: #fce7f3; font-weight: bold; }
        .separator { border-bottom: 4px solid #f9a8d4; margin: 15px 0; }
        .grand-total { background: #fbcfe8; border: 2px solid #ec4899; padding: 12px; text-align: right; margin-top: 15px; font-size: 14px; }
        .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #666; }
        @media print {
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .lot-section { page-break-inside: avoid; }
        }
      </style></head>
      <body>${printContent.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
  };

  const calcTotal = () => pendingItems.reduce((s, i) => s + (i.quantity * i.unit_price), 0);

  if (loading) return <div className="min-h-screen bg-pink-50 flex items-center justify-center"><p>Chargement...</p></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin/dashboard")}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
            <h1 className="text-2xl font-bold text-pink-600"><FileText className="inline w-6 h-6 mr-2" />Bons de Livraison</h1>
          </div>
          <Dialog open={showNewAccount} onOpenChange={setShowNewAccount}>
            <DialogTrigger asChild><Button className="bg-pink-500 hover:bg-pink-600"><Plus className="w-4 h-4 mr-2" />Nouveau Compte</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau Compte Pro</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Nom</Label><Input value={newAccountName} onChange={(e) => setNewAccountName(e.target.value)} placeholder="Ex: iPressing" /></div>
                <Button onClick={createAccount} className="w-full bg-pink-500">Créer</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="border-pink-200">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5" />Comptes</CardTitle></CardHeader>
            <CardContent>
              {accounts.length === 0 ? <p className="text-gray-500 text-sm">Aucun compte</p> : (
                <div className="space-y-2">
                  {accounts.map((account) => (
                    <div key={account.id} className={`p-3 rounded-lg border cursor-pointer ${selectedAccount?.id === account.id ? "border-pink-500 bg-pink-50" : "border-gray-200 hover:border-pink-300"}`} onClick={() => setSelectedAccount(account)}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{account.name}</span>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); deleteAccount(account.id); }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                      <div className="text-xs text-gray-500">{account.prices?.length || 0} tarifs</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="lg:col-span-2">
            {selectedAccount ? (
              <div className="space-y-4">
                <Card className="border-pink-200">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Tarifs - {selectedAccount.name}</CardTitle>
                    <Dialog open={showAddArticle} onOpenChange={setShowAddArticle}>
                      <DialogTrigger asChild><Button size="sm" variant="outline" className="border-green-400 text-green-600"><Plus className="w-4 h-4 mr-1" />Ajouter</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Nouvel Article</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                          <div><Label>Nom</Label><Input value={newArticleName} onChange={(e) => setNewArticleName(e.target.value)} placeholder="Ex: Chemise" /></div>
                          <div><Label>Prix (€)</Label><Input type="number" step="0.01" value={newArticlePrice} onChange={(e) => setNewArticlePrice(e.target.value)} /></div>
                          <Button onClick={addArticleToAccount} className="w-full bg-green-500">Ajouter</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {selectedAccount.prices?.map((price, i) => (
                        <span key={i} className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">{price.name}: {price.price.toFixed(2)}€</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Dialog open={showNewSlip} onOpenChange={(open) => { setShowNewSlip(open); if (!open) { setPendingItems([]); setCurrentLotItems([]); setLotNumber(""); } }}>
                  <DialogTrigger asChild><Button className="w-full bg-green-500 hover:bg-green-600"><Plus className="w-4 h-4 mr-2" />Nouveau Bon</Button></DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Nouveau Bon - {selectedAccount.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      
                      {/* Lots déjà validés */}
                      {pendingItems.length > 0 && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="font-medium text-green-700 mb-2">Lots validés:</p>
                          {Object.entries(pendingItems.reduce((acc, item) => {
                            if (!acc[item.marking_number]) acc[item.marking_number] = [];
                            acc[item.marking_number].push(item);
                            return acc;
                          }, {})).map(([num, items]) => (
                            <div key={num} className="p-2 bg-white rounded mb-2 border-l-4 border-green-500">
                              <span className="font-bold text-pink-600">Lot N°{num}</span>
                              <div className="text-sm text-gray-600">
                                {items.map((it, i) => `${it.quantity}x ${it.article}`).join(", ")}
                              </div>
                              <div className="text-sm font-medium">{items.reduce((s, i) => s + i.quantity * i.unit_price, 0).toFixed(2)}€</div>
                            </div>
                          ))}
                          <p className="font-bold mt-2 text-green-700">Total: {calcTotal().toFixed(2)}€</p>
                        </div>
                      )}

                      {/* Lot en cours de création */}
                      <div className="p-4 border-2 border-dashed border-pink-300 rounded-lg bg-pink-50">
                        <p className="font-medium text-pink-700 mb-3">Créer un lot:</p>
                        
                        <div className="mb-3">
                          <Label>N° du lot (sac)</Label>
                          <Input value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} placeholder="Ex: 5021" className="max-w-xs" />
                        </div>

                        {/* Articles déjà ajoutés au lot en cours */}
                        {currentLotItems.length > 0 && (
                          <div className="mb-3 p-2 bg-white rounded border">
                            <p className="text-sm font-medium mb-1">Articles dans ce lot:</p>
                            {currentLotItems.map((item, i) => (
                              <div key={i} className="flex justify-between items-center text-sm py-1">
                                <span>{item.quantity}x {item.article} ({(item.quantity * item.unit_price).toFixed(2)}€)</span>
                                <Button variant="ghost" size="sm" onClick={() => removeCurrentLotItem(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                              </div>
                            ))}
                            <p className="text-sm font-medium mt-1 pt-1 border-t">Sous-total lot: {currentLotItems.reduce((s, i) => s + i.quantity * i.unit_price, 0).toFixed(2)}€</p>
                          </div>
                        )}

                        {/* Ajouter un article au lot */}
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <Label className="text-xs">Article</Label>
                            <select value={lotArticle} onChange={(e) => setLotArticle(e.target.value)} className="w-full h-10 px-3 border rounded-md">
                              <option value="">Choisir...</option>
                              {selectedAccount.prices?.map((p) => <option key={p.name} value={p.name}>{p.name} ({p.price}€)</option>)}
                            </select>
                          </div>
                          <div className="w-20">
                            <Label className="text-xs">Qté</Label>
                            <Input type="number" min="1" value={lotQuantity} onChange={(e) => setLotQuantity(parseInt(e.target.value) || 1)} />
                          </div>
                          <Button onClick={addItemToPending} variant="outline" size="sm"><Plus className="w-4 h-4" /></Button>
                        </div>
                        
                        <div className="flex gap-2 mt-3">
                          <Button onClick={validateCurrentLot} disabled={currentLotItems.length === 0} className="bg-pink-500 hover:bg-pink-600">
                            Valider ce lot ({currentLotItems.length} article(s))
                          </Button>
                        </div>
                      </div>

                      <Button onClick={createSlip} disabled={pendingItems.length === 0} className="w-full bg-green-600 hover:bg-green-700 text-lg py-6">
                        Créer le Bon ({Object.keys(pendingItems.reduce((acc, i) => { acc[i.marking_number] = true; return acc; }, {})).length} lot(s) - {calcTotal().toFixed(2)}€)
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Card className="border-pink-200">
                  <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Package className="w-5 h-5" />Historique</CardTitle></CardHeader>
                  <CardContent>
                    {slips.length === 0 ? <p className="text-gray-500 text-sm">Aucun bon</p> : (
                      <div className="space-y-2">
                        {slips.map((slip) => (
                          <div key={slip.id} className={`p-3 border rounded-lg ${slip.is_paid ? 'bg-green-50 border-green-200' : ''}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-medium flex items-center gap-2">
                                  {slip.slip_number}
                                  {slip.is_paid && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Payé</span>}
                                  {slip.customer_name && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">→ {slip.customer_name}</span>}
                                </div>
                                <div className="text-sm text-gray-500">{slip.date} - {slip.items?.length || 0} article(s)</div>
                                <SlipDiscountLine slip={slip} />
                              </div>
                              <div className="flex items-center gap-1 flex-wrap justify-end">
                                <span className="font-bold text-pink-600 mr-2">{slip.total?.toFixed(2)}€</span>
                                <Button variant="outline" size="icon" onClick={() => setDiscountSlip(slip)} title="Remise" className="text-orange-600 border-orange-300 hover:bg-orange-50 font-bold" data-testid={`slip-discount-btn-${slip.id}`}>%</Button>
                                <Button 
                                  variant={slip.is_paid ? "default" : "outline"} 
                                  size="icon" 
                                  onClick={() => markAsPaid(slip.id)} 
                                  title={slip.is_paid ? "Marquer non payé" : "Marquer payé"}
                                  className={slip.is_paid ? "bg-green-500 hover:bg-green-600" : "text-green-600 border-green-300 hover:bg-green-50"}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="icon" onClick={() => setShowTransferDialog(slip)} title="Transférer au client" className="text-blue-600 border-blue-300 hover:bg-blue-50"><Send className="w-4 h-4" /></Button>
                                <Button variant="outline" size="icon" onClick={() => openEditSlip(slip)} title="Modifier"><svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></Button>
                                <Button variant="outline" size="icon" onClick={() => setPrintSlip(slip)} title="Imprimer"><Printer className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => deleteSlip(slip.id)} title="Supprimer"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="border-pink-200 h-full flex items-center justify-center">
                <CardContent className="text-center text-gray-500 py-12"><Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" /><p>Sélectionnez un compte</p></CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Dialog d'édition de bon */}
      {editingSlip && (
        <Dialog open={!!editingSlip} onOpenChange={() => { setEditingSlip(null); setEditItems([]); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Modifier le bon {editingSlip.slip_number}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-gray-500">Client: {editingSlip.account_name} | Date: {editingSlip.date}</div>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-pink-100">
                    <tr>
                      <th className="p-2 text-left">N° Lot</th>
                      <th className="p-2 text-left">Article</th>
                      <th className="p-2 text-center w-20">Qté</th>
                      <th className="p-2 text-right w-24">Prix</th>
                      <th className="p-2 text-right w-24">Total</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {editItems.map((item, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">
                          <Input value={item.marking_number} onChange={(e) => updateEditItem(i, 'marking_number', e.target.value)} className="h-8" />
                        </td>
                        <td className="p-2">
                          <select value={item.article} onChange={(e) => {
                            const price = selectedAccount?.prices?.find(p => p.name === e.target.value)?.price || item.unit_price;
                            const newItems = [...editItems];
                            newItems[i] = { ...newItems[i], article: e.target.value, unit_price: price, total: newItems[i].quantity * price };
                            setEditItems(newItems);
                          }} className="w-full h-8 px-2 border rounded">
                            <option value="">Choisir...</option>
                            {selectedAccount?.prices?.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                          </select>
                        </td>
                        <td className="p-2">
                          <Input type="number" min="1" value={item.quantity} onChange={(e) => updateEditItem(i, 'quantity', parseInt(e.target.value) || 1)} className="h-8 text-center" />
                        </td>
                        <td className="p-2 text-right">{item.unit_price?.toFixed(2)}€</td>
                        <td className="p-2 text-right font-medium">{(item.quantity * item.unit_price).toFixed(2)}€</td>
                        <td className="p-2">
                          <Button variant="ghost" size="icon" onClick={() => removeEditItem(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <Button variant="outline" onClick={addEditItem}><Plus className="w-4 h-4 mr-1" />Ajouter un article</Button>
              
              <div className="flex justify-between items-center p-3 bg-pink-100 rounded-lg">
                <span className="font-bold">TOTAL:</span>
                <span className="font-bold text-xl text-pink-600">{calcEditTotal().toFixed(2)}€</span>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setEditingSlip(null); setEditItems([]); }}>Annuler</Button>
                <Button onClick={saveEditSlip} className="bg-pink-500 hover:bg-pink-600">Enregistrer les modifications</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {printSlip && (
        <Dialog open={!!printSlip} onOpenChange={() => setPrintSlip(null)}>
          <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Aperçu du Bon - {printSlip.slip_number}</DialogTitle></DialogHeader>
            <div ref={printRef} className="p-4 bg-white text-sm">
              <div className="text-center mb-4">
                <h2 className="text-lg font-bold text-pink-600">PRESSING SAKURA</h2>
                <p className="text-xs text-gray-500">Bon de Livraison</p>
              </div>
              <div className="border-b pb-3 mb-3 grid grid-cols-2 gap-2 text-xs">
                <p><strong>Bon N°:</strong> {printSlip.slip_number}</p>
                <p><strong>Date:</strong> {printSlip.date}</p>
                <p><strong>Client:</strong> {printSlip.account_name}</p>
                <p><strong>Nb lots:</strong> {Object.keys(printSlip.items?.reduce((acc, i) => { acc[i.marking_number] = true; return acc; }, {}) || {}).length}</p>
              </div>
              
              {/* Grouper par lot */}
              {Object.entries(printSlip.items?.reduce((acc, item) => {
                if (!acc[item.marking_number]) acc[item.marking_number] = [];
                acc[item.marking_number].push(item);
                return acc;
              }, {}) || {}).map(([lotNum, items], lotIndex, allLots) => {
                const lotTotal = items.reduce((s, i) => s + (i.total || i.quantity * i.unit_price), 0);
                return (
                  <div key={lotNum} className="mb-3">
                    <div className="bg-pink-200 p-2 font-bold text-pink-800 border border-pink-300">
                      LOT N° {lotNum}
                    </div>
                    <table className="w-full border-collapse text-xs">
                      <thead>
                        <tr className="bg-pink-50">
                          <th className="border p-1 text-left">Article</th>
                          <th className="border p-1 text-center w-16">Qté</th>
                          <th className="border p-1 text-right w-20">P.U.</th>
                          <th className="border p-1 text-right w-20">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, i) => (
                          <tr key={i}>
                            <td className="border p-1">{item.article}</td>
                            <td className="border p-1 text-center">{item.quantity}</td>
                            <td className="border p-1 text-right">{item.unit_price?.toFixed(2)}€</td>
                            <td className="border p-1 text-right">{(item.total || item.quantity * item.unit_price)?.toFixed(2)}€</td>
                          </tr>
                        ))}
                        <tr className="bg-pink-100 font-bold">
                          <td colSpan="3" className="border p-1 text-right">Total Lot {lotNum}:</td>
                          <td className="border p-1 text-right">{lotTotal.toFixed(2)}€</td>
                        </tr>
                      </tbody>
                    </table>
                    {lotIndex < allLots.length - 1 && <div className="border-b-4 border-pink-300 my-3"></div>}
                  </div>
                );
              })}

              <div className="mt-4 p-3 bg-pink-200 border-2 border-pink-400 text-right">
                {printSlip.discount_amount > 0 && (
                  <p className="text-sm">Sous-total : {printSlip.subtotal?.toFixed(2)}€ — Remise{printSlip.discount_type === "percent" ? ` (${printSlip.discount_value}%)` : ""} : -{printSlip.discount_amount.toFixed(2)}€</p>
                )}
                <span className="font-bold text-lg">TOTAL GÉNÉRAL: </span>
                <span className="font-bold text-xl text-pink-700">{printSlip.total?.toFixed(2)}€</span>
              </div>
              
              <div className="mt-4 text-center text-xs text-gray-500">
                <p>Pressing Sakura - Merci de votre confiance</p>
              </div>
            </div>
            <div className="flex justify-between items-center border-t pt-3">
              <span className="text-sm text-gray-500">{printSlip.items?.length} articles - {Object.keys(printSlip.items?.reduce((acc, i) => { acc[i.marking_number] = true; return acc; }, {}) || {}).length} lots</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setPrintSlip(null)}>Fermer</Button>
                <Button onClick={handlePrint} className="bg-pink-500"><Printer className="w-4 h-4 mr-2" />Imprimer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {discountSlip && (
        <SlipDiscountDialog
          slip={discountSlip}
          headers={getAuthHeaders()}
          onClose={() => setDiscountSlip(null)}
          onSaved={() => { setDiscountSlip(null); if (selectedAccount) fetchSlips(selectedAccount.id); }}
        />
      )}

      {/* Dialog de transfert */}
      {showTransferDialog && (
        <Dialog open={!!showTransferDialog} onOpenChange={() => setShowTransferDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Transférer le bon {showTransferDialog.slip_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Sélectionnez un client professionnel pour lui transférer ce bon. 
                Le client pourra ensuite le consulter dans son espace.
              </p>
              
              {proCustomers.length === 0 ? (
                <p className="text-center text-gray-500 py-4">Aucun client professionnel trouvé</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {proCustomers.map((customer) => (
                    <div 
                      key={customer.id} 
                      className={`p-3 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors ${showTransferDialog.customer_id === customer.id ? 'bg-blue-100 border-blue-400' : ''}`}
                      onClick={() => transferToCustomer(showTransferDialog.id, customer.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{customer.first_name} {customer.last_name}</p>
                          <p className="text-sm text-gray-500">{customer.email}</p>
                        </div>
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowTransferDialog(null)}>Annuler</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminDeliverySlips;
